import { Component, inject, Input, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Role } from "@app/core/domain/user";
import { Constants } from "@core/constants";
import { CourseDetail, CoursePeriod, CourseYear } from "@core/domain/course-detail";
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  ModalController,
} from "@ionic/angular/standalone";
import { TranslatePipe } from "@ngx-translate/core";
import { CourseData, SelectedPeriod } from "@shared/types/course-data";
import { NgxPermissionsModule } from "ngx-permissions";
import { TimeslotComponent } from "../../components/timeslot/timeslot.component";

@Component({
  selector: "pa-flexible-plan-element",
  templateUrl: "./flexible-plan-element-modal.component.html",
  styleUrls: ["./flexible-plan-element-modal.component.scss"],
  imports: [
    FormsModule,
    TimeslotComponent,
    NgxPermissionsModule,
    TranslatePipe,
    IonHeader,
    IonToolbar,
    IonButton,
    IonTitle,
    IonIcon,
    IonContent,
    IonSelect,
    IonSelectOption,
    IonCheckbox,
    IonFooter,
  ],
})
export class FlexiblePlanElementModalComponent implements OnInit {
  @Input() courseData!: CourseData;
  @Input() courseDetail!: CourseDetail;
  @Input() showMoveToPlanLaterButton = false;

  academicYear = 0;
  selectedYear!: CourseYear | undefined;
  selectedYearCoursePeriods!: CoursePeriod[] | undefined;
  selectedYearFlexibleTimeslots: string[] = [];
  selectedPeriods: CoursePeriod[] = [];
  checkedSelectedPeriods: boolean[] = [];

  protected readonly Role = Role;

  private readonly modalCtrl = inject(ModalController);

  ngOnInit(): void {
    if (this.courseDetail.courseYears.length > 0) {
      document.documentElement.style.setProperty(
        "--element-period-columns",
        this.courseDetail.courseYears[0].periods.length.toString(),
      );
    }
  }

  async dismiss(): Promise<void> {
    try {
      await this.modalCtrl.dismiss(null, Constants.BUTTON_ACTION_CANCEL);
    } catch (error) {
      console.error("Error during canceling of Flexible Plan Modal:", error);
    }
  }

  selectPeriod(year: CourseYear | undefined, period: CoursePeriod): void {
    if (year) {
      if (!year.flexiblePlanCourse) {
        // This academic year the course can not be planned flexible, so only one period can be selected
        if (period.offered) {
          const selectedPeriod: SelectedPeriod = { year: year, period: period };
          this.modalCtrl.dismiss(selectedPeriod, Constants.BUTTON_ACTION_OK).catch((error) => {
            console.error("Error during dismissing Flexible Plan Modal:", error);
          });
        }
        return;
      }

      // This academic year the course can be planned flexible, so multiple periods can be selected
      const indexSelectedPeriod = this.selectedPeriods.findIndex(
        (coursePeriod) => coursePeriod.periodNr === period.periodNr,
      );
      if (indexSelectedPeriod >= 0) {
        this.selectedPeriods.splice(indexSelectedPeriod, 1);
      } else {
        this.selectedPeriods.push(period);
      }
      this.checkedSelectedPeriods[period.periodNr] = !this.checkedSelectedPeriods[period.periodNr];
    }
  }

  startYearChanged(academicYear: number): void {
    this.selectedYear = this.courseDetail.courseYears.find((year) => year.academicYear === academicYear);
    this.selectedYearCoursePeriods = this.selectedYear?.periods;
    if (this.selectedYear?.flexiblePlanCourse && this.selectedYearCoursePeriods?.[0]?.timeslots?.length) {
      // this academic year the course can be planned flexible, and each period will show a checkbox,
      // so the timeslots must be shown next to the selected year
      this.selectedYearFlexibleTimeslots = this.selectedYearCoursePeriods[0].timeslots;
    } else {
      this.selectedYearFlexibleTimeslots = [];
    }
  }

  confirmFlexiblePeriodsSelection(): void {
    this.selectedPeriods.sort((period1, period2) => period1.periodNr - period2.periodNr);

    this.selectedPeriods[0].ribbonPeriods = this.selectedPeriods.map((period) => period.periodNr);

    if (this.selectedYearCoursePeriods) {
      // time slots first period
      this.selectedPeriods[0].timeslots = this.selectedYearCoursePeriods[0].timeslots;
    }

    const selectedPeriod = { year: this.selectedYear, period: this.selectedPeriods[0] } as SelectedPeriod;
    this.modalCtrl.dismiss(selectedPeriod, Constants.BUTTON_ACTION_OK).catch((error) => {
      console.error("Error during dismissing Flexible Plan Modal:", error);
    });
  }

  moveToPlanLater(): void {
    this.modalCtrl.dismiss(null, Constants.BUTTON_ACTION_PLANLATER).catch((error) => {
      console.error("Error during dismissing Flexible Plan Modal for Plan Later:", error);
    });
  }
}
